import { useState, useEffect } from "react";

const ReusableTable = ({
  data,
  columns,
  loading,
  onFilterChange,
  filterConfig,
  excludeFilterColumns = [],
  filterable = true,
}) => {
  const [filteredData, setFilteredData] = useState(data);
  const [isFiltering, setIsFiltering] = useState(false);
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [activeFilters, setActiveFilters] = useState({});
  const [tempFilters, setTempFilters] = useState({});

  useEffect(() => {
    if (showFilterMenu) {
      setTempFilters(activeFilters);
    }
  }, [showFilterMenu]);

  useEffect(() => {
    if (!filterable) {
      setFilteredData(data);
      setIsFiltering(false);
      if (onFilterChange) {
        onFilterChange(false);
      }
      return;
    }
    setFilteredData(data);
  }, [data, loading, filterable, onFilterChange]);

  useEffect(() => {
    // Skip if filterable is false
    if (!filterable) {
      return;
    }
    
    // Apply column-specific filters
    let result = data;

    if (filterable) {
      Object.entries(activeFilters).forEach(([field, filterValue]) => {
      if (filterValue) {
        result = result.filter((item) => {
          let value;

          // Handle custom action columns
          if (field === "status_actions") {
            // Filter based on candidate status for status actions
            value = item.status;
          } else if (field === "interview_actions") {
            // Filter based on interview action state
            if (item.status === "Shortlisted") {
              if (
                item.interviews?.some(
                  (interview) => interview.status === "Scheduled"
                )
              ) {
                value = "Interview Scheduled";
              } else {
                value = "Schedule Interview";
              }
            } else if (item.status === "Pending") {
              value = "Review Required";
            } else {
              value = "Not Available";
            }
          } else if (field === "candidate_status") {
            // Handle nested candidate.status field
            value = item.candidate?.status;
          } else {
            // Handle other fields with dot notation
            value = field
              .split(".")
              .reduce((acc, subKey) => (acc ? acc[subKey] : null), item);
          }

          // Handle date filtering
          if (
            field === "to_date" ||
            field === "deadline" ||
            field === "scheduled_at" ||
            field === "created_at" ||
            field === "effective_from" ||
            field === "effective_to" ||
            (Array.isArray(field) && field.includes("to_date"))
          ) {
            if (!value) return false;
            const itemDate = new Date(value).toISOString().split("T")[0];
            try {
              // Debug: verify record date vs selected filter date
              // eslint-disable-next-line no-console
              // console.log("[ReusableTable] Date filter compare", {
              //   field,
              //   rawValue: value,
              //   itemDate,
              //   selected: filterValue,
              // });
            } catch (_) {}
            return itemDate === filterValue;
          }

          // Basic string matching for other fields
          return value
            ?.toString()
            .toLowerCase()
            .includes(filterValue.toLowerCase());
        });
      }
      });
    }

    const filteringNow = filterable && Object.values(activeFilters).some((v) => !!v);
    setIsFiltering(filteringNow);
    setFilteredData(result);

    if (onFilterChange) {
      onFilterChange(filteringNow);
    }
  }, [activeFilters, data, columns, onFilterChange, filterable]);

  const handleFilterChange = (field, value) => {
    setActiveFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const toggleFilterMenu = () => {
    setShowFilterMenu(!showFilterMenu);
  };

  const clearFilters = () => {
    setActiveFilters({});
  };

  const clearFilterByKey = (key) => {
    const updatedFilters = { ...activeFilters };
    delete updatedFilters[key];
    setActiveFilters(updatedFilters);
  };

  // const filterableColumns = columns.filter(col =>
  //   col.filterable !== false && col.field !== "actions"
  // );

  const filterableColumns = columns.filter(
    (col) =>
      col.filterable !== false &&
      col.field !== "actions" &&
      col.field !== "sr_no" &&
      // Only exclude status column if both status_actions and interview_actions columns exist
      !(
        col.field === "status" &&
        columns.some((c) => c.field === "status_actions") &&
        columns.some((c) => c.field === "interview_actions")
      ) &&
      !excludeFilterColumns.includes(col.field)
  );

  const hasTempFilters = Object.values(tempFilters).some((val) => val !== "");

  return (
    <div className="space-y-4">
      {/* Filter controls */}
      {filterable && (
      <div className="flex flex-col sm:flex-row gap-2 justify-between items-start mb-4">
        {/* filter button */}
        {filterable && (
        <div className="flex justify-between gap-2 w-full sm:w-auto">
          <div className="relative">
            <button
              onClick={toggleFilterMenu}
              className="w-full px-3 py-[6px] border border-gray-400 dark:bg-gray-900 dark:text-white dark:border-gray-700 rounded-md flex gap-1 items-center bg-[#F4F7FF] hover:bg-gray-200"
            >
              <svg
                className="dark:text-white"
                width="18"
                height="17"
                viewBox="0 0 13 14"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7.98734 13.5608C7.85034 13.5608 7.71598 13.5252 7.59884 13.4581L5.09834 12.0415C4.817 11.8812 4.58412 11.6552 4.42215 11.3852C4.26017 11.1153 4.17457 10.8104 4.17359 10.5001V7.29493C4.17408 7.06871 4.0996 6.84803 3.96059 6.6638L0.469345 2.02988C0.387822 1.92291 0.339174 1.79674 0.328769 1.66531C0.318364 1.53387 0.346605 1.40228 0.410378 1.28504C0.474151 1.16781 0.570975 1.06949 0.690169 1.00093C0.809362 0.932381 0.94629 0.896259 1.08584 0.896552H11.8333C11.973 0.896002 12.1101 0.931967 12.2295 1.00047C12.3488 1.06896 12.4458 1.16732 12.5096 1.28465C12.5734 1.40198 12.6016 1.5337 12.5911 1.66523C12.5805 1.79677 12.5317 1.92297 12.4498 2.02988L8.95859 6.6638C8.81933 6.84794 8.74458 7.06863 8.74484 7.29493V12.844C8.74465 13.0338 8.66482 13.2158 8.52285 13.3502C8.38088 13.4845 8.18832 13.5603 7.98734 13.5608ZM1.08584 1.60843L4.57109 6.25297C4.80208 6.55645 4.92547 6.92118 4.92359 7.29493V10.4994C4.92387 10.6874 4.9756 10.8722 5.07376 11.0358C5.17192 11.1994 5.31319 11.3362 5.48384 11.433L7.98434 12.8497L7.99484 7.29422C7.99319 6.92039 8.11685 6.55566 8.34809 6.25226L11.8386 1.61834L1.08584 1.60843Z"
                  fill="currentColor"
                />
              </svg>
              <span className="ml-1">Filter</span>
            </button>

            {showFilterMenu && (
              <div className="absolute z-10 mt-2 md:right-0 right-auto left-0 md:left-auto bg-white dark:bg-gray-900 dark:border-gray-700 border border-gray-200 rounded-md shadow-lg p-4 w-64 overflow-auto h-auto max-h-96">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-medium dark:text-white">Filters</h3>

                  {hasTempFilters && (
                    <button
                      onClick={() => {
                        setActiveFilters(tempFilters);
                        setShowFilterMenu(false);
                      }}
                      className=" bg-blue-600 text-white p-2 rounded-md hover:bg-blue-700 text-sm"
                    >
                      Apply Filters
                    </button>
                  )}

                  {/* {Object.keys(activeFilters).length > 0 && (
                    <button 
                      onClick={clearFilters}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      Clear all
                    </button>
                  )} */}
                </div>

                <div className="space-y-3">
                  {filterableColumns.map((column) => (
                    <div key={column.field} className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-400">
                        {column.label}
                      </label>
                      {column.field === "status" ||
                      column.field === "candidate_status" ||
                      column.field === "recruitment_request_status" ||
                      column.field === "status_actions" ||
                      column.field === "interview_actions" ? (
                        <select
                          value={tempFilters[column.field] || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            setTempFilters((prev) => ({
                              ...prev,
                              [column.field]: value,
                            }));
                          }}
                          className="w-full p-2 dark:bg-gray-950 dark:text-white dark:border-gray-700 text-sm border border-gray-300 rounded-md"
                        >
                          <option value="">All</option>
                          {(() => {
                            let options = [];

                            if (column.field === "status_actions") {
                              // Custom options for Status Actions column
                              options = [
                                { value: "Pending", label: "Pending" },
                                { value: "Shortlisted", label: "Shortlisted" },
                                { value: "Rejected", label: "Rejected" },
                              ];
                            } else if (column.field === "interview_actions") {
                              // Custom options for Interview Actions column
                              options = [
                                {
                                  value: "Schedule Interview",
                                  label: "Schedule Interview",
                                },
                                {
                                  value: "Interview Scheduled",
                                  label: "Interview Scheduled",
                                },
                                {
                                  value: "Review Required",
                                  label: "Review Required",
                                },
                                {
                                  value: "Not Available",
                                  label: "Not Available",
                                },
                              ];
                            } else {
                              // Handle other status fields with existing logic
                              const uniqueStatuses = [
                                ...new Set(
                                  data
                                    .map((item) => {
                                      let value;
                                      if (column.field === "candidate_status") {
                                        // Handle nested candidate.status field
                                        value = item.candidate?.status;
                                      } else {
                                        // Handle other fields with dot notation
                                        value = column.field
                                          .split(".")
                                          .reduce(
                                            (acc, subKey) =>
                                              acc ? acc[subKey] : null,
                                            item
                                          );
                                      }
                                      return value;
                                    })
                                    .filter(Boolean)
                                ),
                              ];

                              options = uniqueStatuses.map((status) => ({
                                value: status,
                                label: status,
                              }));
                            }

                            return options.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ));
                          })()}
                        </select>
                      ) : column.field === "to_date" ||
                        column.field === "deadline" ||
                        column.field === "scheduled_at" ||
                        column.field === "created_at" ||
                        column.field === "effective_from" ||
                        column.field === "effective_to" ||
                        (Array.isArray(column.field) &&
                          column.field.includes("to_date")) ? (
                        <input
                          type="date"
                          value={tempFilters[column.field] || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            setTempFilters((prev) => ({
                              ...prev,
                              [column.field]: value,
                            }));
                            try {
                              // Debug: verify selected date
                              // eslint-disable-next-line no-console
                              // console.log("[ReusableTable] Date filter selected", { field: column.field, selected: value });
                            } catch (_) {}
                          }}
                          className="w-full p-2 text-sm dark:bg-black dark:text-white dark:border-gray-700 dark:focus:outline-none border border-gray-300 rounded-md"
                        />
                      ) : (
                        <input
                          type="text"
                          value={tempFilters[column.field] || ""}
                          // onChange={(e) => handleFilterChange(column.field, e.target.value)}
                          onChange={(e) => {
                            const value = e.target.value;
                            setTempFilters((prev) => ({
                              ...prev,
                              [column.field]: value,
                            }));
                          }}
                          placeholder={`Filter by ${column.label.toLowerCase()}`}
                          className="w-full p-2 text-sm dark:bg-black dark:text-white dark:border-gray-700 dark:focus:outline-none border border-gray-300 rounded-md"
                        />
                      )}
                    </div>
                  ))}
                </div>

                {isFiltering && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-sm text-gray-600">
                      Showing {filteredData.length} of {data.length} results
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {isFiltering && (
            <div className="flex flex-wrap gap-2">
              {Object.entries(activeFilters).map(
                ([key, value]) =>
                  value && (
                    <div
                      key={key}
                      className="flex items-center gap-2 px-3 py-1.5 border border-blue-500 bg-blue-50 text-blue-600 rounded-full text-sm shadow-sm"
                    >
                      <span className="capitalize">
                        {key.replace("_", " ")}: {value}
                      </span>
                      <button
                        onClick={() => clearFilterByKey(key)}
                        className="w-5 h-5 flex items-center justify-center rounded-full hover:bg-blue-100 focus:outline-none"
                        aria-label={`Clear ${key}`}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                  )
              )}
              <button
                onClick={clearFilters}
                className="ml-2 text-sm text-red-500 hover:underline"
              >
                Clear All
              </button>
            </div>
          )}
        </div>
        )}
      </div>
      )}

      {/* Table Container with scroll for small screens */}
      <div className="overflow-x-auto bg-white dark:bg-gray-900 shadow-md rounded-md font-poppins ">
        <table className="min-w-full table-auto md:table-fixed text-sm">
          <thead className="bg-gray-100 dark:bg-gray-800 dark:text-white">
            <tr>
              {columns.map((col, idx) => {
                const align = col.align || "center";
                return (
                  <th
                    key={idx}
                    title={col.label}
                    className={`py-2 px-4 ${
                      align === "center" ? "text-center" : align === "right" ? "text-right" : "text-left"
                    } font-semibold text-xs md:text-sm text-black dark:text-white whitespace-nowrap`}
                  >
                    {col.label}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr className="border-b hover:bg-gray-100 dark:hover:bg-gray-800">
                <td
                  colSpan={columns.length}
                  className="py-2 px-4 text-xs md:text-sm whitespace-nowrap"
                >
                  <div className="animate-spin h-6 w-6 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2 mt-2"></div>
                </td>
              </tr>
            ) : filteredData.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="py-4 px-4 text-center text-gray-500 dark:text-white"
                >
                  No Data Found
                </td>
              </tr>
            ) : (
              filteredData.map((row, idx) => (
                <tr
                  key={idx}
                  className="border-b dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-950 dark:hover:text-white"
                >
                  {columns.map((col, colIdx) => {
                    const align = col.align || "center";
                    return (
                      <td
                        key={colIdx}
                        className={`py-2 px-4 text-xs dark:text-white ${
                          align === "center" ? "text-center" : align === "right" ? "text-right" : "text-left"
                        } ${
                          col.field !== "actions" && col.field !== "action" ? "truncate" : ""
                        } md:text-sm whitespace-nowrap`}
                      >
                        {col.render
                          ? col.render(row, idx)
                          : col.field.includes(".")
                          ? col.field
                              .split(".")
                              .reduce(
                                (acc, subKey) => (acc ? acc[subKey] : "-"),
                                row
                              )
                          : row[col.field] || "-"}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ReusableTable;
